import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { HelperText, Text } from 'react-native-paper';

import { PrimaryButton } from '@/components/atoms/button/PrimaryButton';
import { InlineBanner } from '@/components/atoms/banner/InlineBanner';
import { DigimaLogoFull } from '@/components/atoms/illustrations/DigimaLogoFull';
import { useTranslation } from '@/i18n';
import { spacing } from '@/theme';
import type { AuthError } from '@/stores';

interface SignInCardProps {
  error: AuthError | null;
  isLoading: boolean;
  onSignIn: () => void;
}

export const SignInCard = ({ error, isLoading, onSignIn }: SignInCardProps) => {
  const { t } = useTranslation();
  const [showDetail, setShowDetail] = useState(false);

  const translatedError = error ? t(error.key, { defaultValue: error.key }) : null;

  const toggleDetail = () => {
    setShowDetail((prev) => !prev);
  };

  return (
    <View style={styles.container}>
      <DigimaLogoFull />
      <InlineBanner
        title={t('auth.signInAlertTitle')}
        message={t('auth.signInSubtitle')}
        variant="info"
        textAlign="center"
        testID="sign-in-alert"
      />
      <View style={styles.buttonContainer}>
        <PrimaryButton disabled={isLoading} loading={isLoading} onPress={onSignIn}>
          {t('auth.signInButton')}
        </PrimaryButton>
      </View>
      <View style={styles.errorContainer}>
        <HelperText type="error" visible={error !== null}>
          {translatedError}
        </HelperText>
        {error?.detail && (
          <Pressable onPress={toggleDetail} style={styles.detailsToggle}>
            <Text style={styles.detailsLabel} variant="labelSmall">
              {showDetail ? '▲' : '▼'} {t('common.details')}
            </Text>
            {showDetail && (
              <Text style={styles.detailText} variant="bodySmall">
                {error.detail}
              </Text>
            )}
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    gap: spacing[4],
    maxWidth: 360,
    width: '100%',
  },
  buttonContainer: {
    alignSelf: 'stretch',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
  },
  errorContainer: {
    alignSelf: 'stretch',
  },
  detailsToggle: {
    alignSelf: 'flex-start',
  },
  detailsLabel: {
    opacity: 0.5,
  },
  detailText: {
    fontFamily: 'monospace',
    marginTop: spacing[1],
    opacity: 0.6,
  },
});
